/**
 * **How old what is owed is** (M3 · Treasury): one counterparty's postings on its control accounts, aged.
 *
 * Three rules the books do not always decide, said here and in every answer built on this rather than implied:
 *
 * - **Overdue from the due date where the line states one; otherwise as old as its posting.** SAF-T lets a
 *   line carry a `DueDate`, and where the file fills it a charge is *not yet due* or so many days *overdue*
 *   (Thomas, 2026-10-07). Where it does not, the charge is aged from the date it was booked, because an
 *   *overdue* built on a guessed due date is the kind of wrong that reminds a customer who is not late.
 * - **A payment settles what the ledger ties it to first, then the oldest charge** (Thomas, 2026-10-07). A
 *   payment carrying a charge's KID (`CID`) settles that charge, then one carrying its `ReferenceNumber`,
 *   whatever their order in time. What is left — payments that name nothing the charges carry — settles the
 *   oldest charge first. That is how a statement is usually read, and it is a rule rather than a fact: a
 *   customer who paid March and not February, and said so in no reference, shows February as paid.
 * - **What was carried into the year has no date of its own.** A customer's opening is the sum of whatever was
 *   open on the first day, so it is *carried in* rather than spread across ages it cannot be read for. Being
 *   the oldest, it is settled first.
 *
 * Amounts are in the direction of what is owed: positive is outstanding, whichever side of the ledger it sits
 * on. A receivable is owed to us when the customer's balance is a debit; a payable is owed by us when the
 * supplier's is a credit.
 */

/** One posting, or a carried-in opening (`date` null), on one counterparty's control accounts. */
export interface AgedItem {
  /** ISO yyyy-mm-dd; null for an opening carried into the year, which has no date of its own. */
  date: string | null;
  /** As the ledger holds it: debit positive. */
  amountMinor: bigint;
  /** When the line says the charge falls due (SAF-T `DueDate`). Absent where it says nothing. */
  dueDate?: string | null;
  /** The KID the line carries (SAF-T `CID`): a payment and a charge carrying the same one are matched first. */
  cid?: string | null;
  /** The line's `ReferenceNumber`, such as an invoice number: matched next, before oldest-first. */
  reference?: string | null;
}

export interface Aged {
  /** Still open from what was carried into the year. */
  carriedInMinor: bigint;
  /** Still open and not yet due at the date asked about. Only a charge whose line states a due date can be. */
  notDueMinor: bigint;
  /**
   * Still open, by days past the due date where the line states one, and otherwise by days since posting.
   * `fromDue` and `fromPosting` say how many charges were aged each way, so an answer can say which it is.
   */
  days0to30Minor: bigint;
  days31to60Minor: bigint;
  days61to90Minor: bigint;
  over90Minor: bigint;
  /** Paid or credited beyond every charge: held by the counterparty, so it has no age. */
  creditMinor: bigint;
  /** Everything open less the credit: the counterparty's balance, as owed. */
  netMinor: bigint;
  /** The posting date of the oldest charge still open; null when nothing dated is open. */
  oldestOpen: string | null;
  /** Open dated charges aged from their due date. */
  fromDue: number;
  /** Open dated charges aged from their posting date, because their line states no due date. */
  fromPosting: number;
}

export const NOTHING_AGED: Aged = Object.freeze({
  carriedInMinor: 0n,
  notDueMinor: 0n,
  days0to30Minor: 0n,
  days31to60Minor: 0n,
  days61to90Minor: 0n,
  over90Minor: 0n,
  creditMinor: 0n,
  netMinor: 0n,
  oldestOpen: null,
  fromDue: 0,
  fromPosting: 0,
});

/** Days since 1 January 1970, for a yyyy-mm-dd: the one arithmetic every date here needs. */
export const dayNumber = (iso: string): number =>
  Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10))) /
  86_400_000;

/** One charge, after every payment up to the date asked about was applied to the oldest charge first. */
export interface SettledCharge {
  /** Its posting date; null for what was carried into the year. */
  date: string | null;
  /** When its line says it falls due; null where it says nothing. */
  dueDate: string | null;
  /** The charge, as owed. */
  minor: bigint;
  /** What of it is still open. */
  openMinor: bigint;
  /**
   * The date of the payment that completed it: what a customer's habit is read from. Null while any of it is
   * open, and for a charge completed by something carried in, which has no date either.
   */
  settledOn: string | null;
}

export interface Settlement {
  /** Every charge, oldest first. */
  charges: SettledCharge[];
  /** Paid or credited beyond every charge. */
  creditMinor: bigint;
}

/** The later of two dates, where no date at all is the earliest there is. */
const later = (a: string | null, b: string | null) => (a === null || (b !== null && b > a) ? b : a);

/**
 * The items in the order they happened, each payment settling first the charges the ledger ties it to — the
 * same KID, then the same reference — and then the oldest charge still open. What was carried in comes first;
 * on one day a charge comes before a payment, so an invoice paid the day it was raised took no days; otherwise
 * the ledger's own order holds. A payment with nothing left to settle is a credit, which the next charge takes
 * first.
 */
export function settle(
  items: readonly AgedItem[],
  side: 'receivable' | 'payable',
  asOf: string,
): Settlement {
  // One key per item, so the order is decided by data rather than by which pairs the engine compares: the day
  // (none, as the first day there is, sorts first), then a charge before a payment, then the ledger's own order, which no two share.
  const events = items
    .filter((item) => item.date === null || item.date <= asOf)
    .map((item, order) => {
      const minor = side === 'receivable' ? item.amountMinor : -item.amountMinor;
      const key = `${item.date ?? '0000-00-00'}|${minor > 0n ? 0 : 1}|${String(order).padStart(9, '0')}`;
      const charge: SettledCharge | null =
        minor > 0n
          ? {
              date: item.date,
              dueDate: item.dueDate ?? null,
              minor,
              openMinor: minor,
              settledOn: null,
            }
          : null;
      // What a payment has left to settle, as the two passes below take from it.
      return {
        date: item.date,
        key,
        charge,
        unpaid: minor > 0n ? 0n : -minor,
        ties: { cid: item.cid ?? null, reference: item.reference ?? null },
      };
    })
    .sort((a, b) => (a.key < b.key ? -1 : 1));
  const charges = events.flatMap((event) => (event.charge === null ? [] : [event.charge]));

  // **First, what the ledger ties together**, whatever the order in time: the KID, then the reference.
  for (const tie of ['cid', 'reference'] as const)
    for (const payment of events) {
      const value = payment.ties[tie];
      if (payment.charge !== null || value === null) continue;
      for (const event of events) {
        const charge = event.charge;
        if (payment.unpaid === 0n) break;
        if (charge === null || event.ties[tie] !== value || charge.openMinor === 0n) continue;
        const taken = payment.unpaid < charge.openMinor ? payment.unpaid : charge.openMinor;
        charge.openMinor -= taken;
        payment.unpaid -= taken;
        // Paid before it was raised is paid on the day it was raised.
        if (charge.openMinor === 0n) charge.settledOn = later(payment.date, charge.date);
      }
    }

  // **Then the oldest charge still open**, for whatever no tie settled.
  const open: SettledCharge[] = [];
  let credit = 0n;
  for (const event of events) {
    const charge = event.charge;
    if (charge !== null) {
      if (charge.openMinor === 0n) continue;
      const taken = credit < charge.openMinor ? credit : charge.openMinor;
      credit -= taken;
      charge.openMinor -= taken;
      if (charge.openMinor === 0n) charge.settledOn = event.date;
      else open.push(charge);
      continue;
    }
    let paying = event.unpaid;
    for (const head of open) {
      if (paying === 0n) break;
      const taken = paying < head.openMinor ? paying : head.openMinor;
      head.openMinor -= taken;
      paying -= taken;
      if (head.openMinor === 0n) head.settledOn = event.date;
    }
    // Drop what this payment completed from the front of the queue, and nothing still open.
    const stillOpen = open.findIndex((head) => head.openMinor > 0n);
    open.splice(0, stillOpen === -1 ? open.length : stillOpen);
    credit += paying;
  }
  return { charges, creditMinor: credit };
}

/**
 * What of these items is still open at the end of `asOf`, by age. `receivable` reads a debit as a charge and a
 * credit as a payment; `payable` the other way round. Items dated after `asOf` had not happened yet.
 */
export function age(
  items: readonly AgedItem[],
  side: 'receivable' | 'payable',
  asOf: string,
): Aged {
  const { charges, creditMinor } = settle(items, side, asOf);
  const out = { ...NOTHING_AGED, creditMinor };
  for (const charge of charges) {
    if (charge.openMinor === 0n) continue;
    if (charge.date === null) {
      out.carriedInMinor += charge.openMinor;
      continue;
    }
    out.oldestOpen ??= charge.date;
    // Overdue from the date it falls due where the line says, and not yet due until the day after it.
    if (charge.dueDate === null) out.fromPosting += 1;
    else {
      out.fromDue += 1;
      if (charge.dueDate >= asOf) {
        out.notDueMinor += charge.openMinor;
        continue;
      }
    }
    const days = dayNumber(asOf) - dayNumber(charge.dueDate ?? charge.date);
    if (days <= 30) out.days0to30Minor += charge.openMinor;
    else if (days <= 60) out.days31to60Minor += charge.openMinor;
    else if (days <= 90) out.days61to90Minor += charge.openMinor;
    else out.over90Minor += charge.openMinor;
  }
  out.netMinor =
    out.carriedInMinor +
    out.notDueMinor +
    out.days0to30Minor +
    out.days31to60Minor +
    out.days61to90Minor +
    out.over90Minor -
    out.creditMinor;
  return out;
}

/** How long a counterparty takes to pay, in days from posting to the payment that completed a charge. */
export interface Behaviour {
  /** A quarter of its charges were paid in full sooner than this. */
  quick: number;
  /** Half were. */
  usual: number;
  /** Three quarters were. */
  slow: number;
  /** How many paid charges it is read from. */
  observed: number;
}

/**
 * **A habit, where there is one to read**: the quartiles of days-to-pay over the dated charges paid in full.
 * Fewer than three is not a habit, so it is null rather than a pattern drawn through two points.
 */
export function behaviourOf(charges: readonly SettledCharge[]): Behaviour | null {
  const days = charges
    .flatMap((charge) =>
      charge.date !== null && charge.settledOn !== null
        ? [dayNumber(charge.settledOn) - dayNumber(charge.date)]
        : [],
    )
    .sort((a, b) => a - b);
  if (days.length < 3) return null;
  // Nearest rank: always one of the observed days, never a value between two that nobody waited.
  const rank = (share: number) => days[Math.ceil(share * days.length) - 1] as number;
  return { quick: rank(0.25), usual: rank(0.5), slow: rank(0.75), observed: days.length };
}

/** Bucket by bucket, for a total over several counterparties; the oldest open date is the earliest of them. */
export function addAged(a: Aged, b: Aged): Aged {
  return {
    carriedInMinor: a.carriedInMinor + b.carriedInMinor,
    notDueMinor: a.notDueMinor + b.notDueMinor,
    days0to30Minor: a.days0to30Minor + b.days0to30Minor,
    days31to60Minor: a.days31to60Minor + b.days31to60Minor,
    days61to90Minor: a.days61to90Minor + b.days61to90Minor,
    over90Minor: a.over90Minor + b.over90Minor,
    creditMinor: a.creditMinor + b.creditMinor,
    netMinor: a.netMinor + b.netMinor,
    oldestOpen:
      a.oldestOpen === null
        ? b.oldestOpen
        : b.oldestOpen === null || a.oldestOpen <= b.oldestOpen
          ? a.oldestOpen
          : b.oldestOpen,
    fromDue: a.fromDue + b.fromDue,
    fromPosting: a.fromPosting + b.fromPosting,
  };
}
